import { useMemo } from "react";
import { RouterProvider } from "react-router-dom";
import { mobileRouter } from "./mobile";
import { useAppSelector } from "../hooks/redux";
import { publicRouter } from "./public";
import { useRouterHook } from "../hooks/useRouterHook";
import {
  amplifyProvider,
  authController,
  circadiaEngineProvider,
  clinicController,
  localStorageProvider,
  notificationController,
  patientController,
  timeProvider,
  watchlistController,
  summaryController,
  homeHealthController,
  pushNotificationProvider,
  smsController,
  userController,
} from "../AppController";
import { appNotification } from "../util/appNotification";
import useSWRImmutable from "swr/immutable";
import { restrictedRouter } from "./restricted";
import { isPIN } from "../util/pin";
import VitalsRedirectHandler from "../components/VitalsRedirectHandler";
import { useVitalsSubdomainRedirect } from "../hooks/useVitalsSubdomainRedirect";
import { useAndroidAuth } from "../hooks/useAndroidAuth";
import { useVitalsAIMode } from "../hooks/useVitalsAIMode";
import { scheduleTokenAutoRefresh } from "../hexagon/core/impl/authServiceImpl";
import { hasProjectNeoAccess } from "../util/subdomain";

export const AppRouter = () => {
  // Handle vitals subdomain redirect - backup check
  useVitalsSubdomainRedirect();

  // Handle Android WebView authentication
  useAndroidAuth();

  // Check if user is in Vitals AI mode (subdomain or beta feature)
  const isVitalsAIMode = useVitalsAIMode();

  const { getAuthState, populateAppData } = useRouterHook({
    amplifyProvider: amplifyProvider,
    authController: authController,
    clinicController: clinicController,
    localStorageProvider: localStorageProvider,
    notificationController: notificationController,
    watchlistController: watchlistController,
    timeProvider: timeProvider,
    appNotification: appNotification,
    patientController: patientController,
    circadiaEngineProvider,
    summaryController,
    homeHealthController,
    isPIN,
    smsController: smsController,
    userController: userController,
  });
  const isBiometric = localStorageProvider.get("isBiometric");
  const getAuthStateP = async () => {
    // Skip PIN for Vitals AI mode users
    if (isPIN(isVitalsAIMode)) {
      const authState = await getAuthState();

      if (isBiometric && isBiometric === "true" && authState === "biometric") {
        await populateAppData();
        // Schedule auto-refresh for SSO users
        if (localStorageProvider.get("sso") === "true") {
          scheduleTokenAutoRefresh();
        }
      }
    } else {
      const authState = await getAuthState();
      if (authState !== "notAuthorized") {
        await Promise.all([
          populateAppData(),
          pushNotificationProvider.registerDeviceForPushNotifications(),
        ]);
        // Schedule auto-refresh for SSO users
        if (localStorageProvider.get("sso") === "true") {
          scheduleTokenAutoRefresh();
        }
      }
    }
  };
  useSWRImmutable("getAuthState", getAuthStateP);

  const {
    isAuthorized,
    isPinAuthorized,
    connectionStatus,
    hasNoAccess,
    userEmail,
    betaFeaturesAllowedList,
  } = useAppSelector((state) => {
    return {
      isAuthorized: state.auth.isAuthorized,
      isPinAuthorized: state.auth.isPinAuthorized,
      hasNoAccess: state.auth.hasNoAccess,
      connectionStatus: state.network.connectionStatus,
      userEmail: state.auth.user?.email,
      betaFeaturesAllowedList: state.auth.betaFeaturesAllowedList,
    };
  });

  // Project Neo is behind a beta gate. Dropping the route entry out of
  // the mobile router tree when access is denied means
  // ``/#/neo`` falls through to the catch-all redirect — unauthorized
  // users can't discover the feature by guessing the URL.
  const hasNeoAccess = hasProjectNeoAccess({
    email: userEmail,
    betaFeaturesAllowedList,
  });

  const mobileRouterInstance = useMemo(
    () => mobileRouter(connectionStatus, { hasNeoAccess }),
    [connectionStatus, hasNeoAccess]
  );

  // Skip PIN requirement for Vitals AI mode users
  if (!isAuthorized || (isPIN(isVitalsAIMode) ? !isPinAuthorized : false)) {
    return <RouterProvider router={publicRouter} />;
  }

  if (hasNoAccess) {
    return <RouterProvider router={restrictedRouter} />;
  }

  // circadiaEngineProvider.changeAppTopAndBottom("#f5f5f5", "#fff");
  return (
    <>
      <VitalsRedirectHandler />
      <RouterProvider router={mobileRouterInstance} />
    </>
  );
};
